import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Save, Search } from "lucide-react";

import { examinationService, type Exam } from "../../services/examinationService";
import { exportAsCsv, exportAsExcel, exportAsPdf } from "../../utils/exportTable";
import { ExportButtons, type ExportFormat } from "../ExportButtons";
import { Field } from "../FormLayout";
import { errorLine } from "../inventory/InventoryParts";
import { Notice, type NoticeState } from "../register/RecordParts";
import { day, gradeFor, sheetDraft, sheetProblems, sheetRows, sheetSummary, stageOf, type SheetEntry } from "./examLogic";

/** One paper's marks: a mark or an absence for each candidate, saved as often as needed, then submitted. */
export function MarkSheetPanel({ exams, examId, onPick, onChanged }: { exams: Exam[]; examId: number | null; onPick: (id: number | null) => void; onChanged: () => void }) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [search, setSearch] = useState("");
  const exam = exams.find((row) => row.id === examId);
  const candidates = useQuery({ queryKey: ["exams", "candidates", examId], queryFn: () => examinationService.getPaperCandidates(examId!), enabled: examId !== null });
  const loaded = useMemo(() => (candidates.data ? sheetDraft(candidates.data) : null), [candidates.data]);
  const [entries, setEntries] = useState<Record<number, SheetEntry>>({});
  useEffect(() => {
    if (loaded) setEntries(loaded);
  }, [loaded]);

  const editable = Boolean(exam?.can_mark && exam.results_status === "draft");
  const problems = exam ? sheetProblems(entries, exam.total_marks) : {};
  const summary = exam ? sheetSummary(entries, exam.total_marks, exam.passing_marks) : null;
  const unsaved = loaded ? Object.keys(entries).some((id) => JSON.stringify(entries[Number(id)]) !== JSON.stringify(loaded[Number(id)])) : false;
  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["exams"] });
    onChanged();
  };
  const save = useMutation({
    mutationFn: () => examinationService.bulkMarks(examId!, sheetRows(entries)),
    onSuccess: () => { setNotice({ tone: "success", text: "The mark sheet is saved. Submit it once every candidate has a mark or is marked absent." }); refresh(); },
  });
  const submit = useMutation({
    mutationFn: () => examinationService.submitResults(examId!),
    onSuccess: () => { setNotice({ tone: "success", text: "Submitted to the head of department for moderation. The sheet is now locked." }); refresh(); },
  });
  const set = (id: number, patch: Partial<SheetEntry>) => setEntries((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  const rows = (candidates.data ?? []).filter((row) => !search.trim() || `${row.student_name} ${row.student_number} ${row.class_name} ${row.seat_number ?? ""}`.toLowerCase().includes(search.trim().toLowerCase()));
  const markable = exams.filter((row) => row.can_mark || row.marked_count > 0);

  const exportSheet = (format: ExportFormat) => {
    if (!exam || !candidates.data) return;
    const columns = [
      { header: "Learner", accessor: (row: (typeof candidates.data)[number]) => row.student_name },
      { header: "Learner number", accessor: (row: (typeof candidates.data)[number]) => row.student_number },
      { header: "Class", accessor: (row: (typeof candidates.data)[number]) => row.class_name },
      { header: "Seat", accessor: (row: (typeof candidates.data)[number]) => row.seat_number ?? "" },
      { header: `Mark (out of ${exam.total_marks})`, accessor: (row: (typeof candidates.data)[number]) => (entries[row.id]?.absent ? "Absent" : entries[row.id]?.marks ?? "") },
      { header: "Grade", accessor: (row: (typeof candidates.data)[number]) => { const entry = entries[row.id]; return entry && !entry.absent && entry.marks !== "" ? gradeFor((Number(entry.marks) / exam.total_marks) * 100) : ""; } },
      { header: "Remark", accessor: (row: (typeof candidates.data)[number]) => entries[row.id]?.remarks ?? "" },
    ];
    const title = `${exam.name} mark sheet (${day(exam.exam_date)})`;
    if (format === "csv") exportAsCsv(`mark-sheet-${exam.id}.csv`, columns, candidates.data);
    if (format === "excel") exportAsExcel(`mark-sheet-${exam.id}.xls`, title, columns, candidates.data);
    if (format === "pdf") exportAsPdf(title, columns, candidates.data);
  };

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <section className="card">
        <div className="card-content grid gap-3 sm:grid-cols-[2fr_1fr]">
          <Field label="Paper" hint={markable.length ? "Papers you teach, and any already marked that you can see." : "No papers to mark: you are not down to teach a subject to a class sitting one."}>
            <select value={examId ?? ""} onChange={(e) => onPick(e.target.value ? Number(e.target.value) : null)} className="input mt-1">
              <option value="">Choose a paper</option>
              {markable.map((row) => <option key={row.id} value={row.id}>{row.name} · {row.class_names.join(", ") || "no classes"} · {stageOf(row).label}</option>)}
            </select>
          </Field>
          {exam && (
            <div className="self-end pb-1 text-sm text-gray-600">
              Out of <strong className="text-gray-900">{exam.total_marks}</strong>, pass at <strong className="text-gray-900">{exam.passing_marks}</strong>
              <span className="block text-xs text-gray-500">{editable ? "You can change marks until the sheet is submitted." : `Read only: ${stageOf(exam).label.toLowerCase()}.`}</span>
            </div>
          )}
        </div>
      </section>

      {exam && (
        <section className="card">
          <div className="card-header flex flex-wrap items-start justify-between gap-3 pb-4">
            <div>
              <h3 className="font-semibold text-gray-900">{exam.name}</h3>
              {summary && (
                <p className="mt-1 text-sm text-gray-500">
                  {summary.marked} marked · {summary.absent} absent · {summary.blank} still blank
                  {summary.average !== null && ` · average ${summary.average.toFixed(1)}% · ${summary.passed} passed`}
                </p>
              )}
            </div>
            <ExportButtons onExport={exportSheet} disabled={!candidates.data?.length} what="the mark sheet" />
          </div>
          <div className="card-content space-y-3">
            {exam.results_status === "draft" && exam.returned_reason && (
              <p className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900"><strong>Sent back by {exam.returned_by_name || "a reviewer"}:</strong> {exam.returned_reason}</p>
            )}
            {(candidates.data?.length ?? 0) > 10 && (
              <label className="relative block w-full max-w-xs">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Find a candidate" aria-label="Find a candidate" className="input h-9 pl-8" />
              </label>
            )}
            {candidates.isLoading ? (
              <p className="rounded-md bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">Loading the candidates…</p>
            ) : !candidates.data?.length ? (
              <p className="rounded-md bg-gray-50 px-3 py-6 text-center text-sm text-gray-500">No candidates are seated for this paper yet. Leadership seats them from its exam period.</p>
            ) : (
              <div className="sheet-wrap">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th className="sheet-rownum">#</th>
                      <th>Candidate</th>
                      <th>Class</th>
                      <th>Seat</th>
                      <th>Mark</th>
                      <th>Absent</th>
                      <th className="text-right">Grade</th>
                      <th>Remark</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, index) => {
                      const entry = entries[row.id] ?? { marks: "", absent: false, remarks: "" };
                      const problem = problems[row.id];
                      const grade = !entry.absent && entry.marks !== "" && !problem ? gradeFor((Number(entry.marks) / exam.total_marks) * 100) : "";
                      return (
                        <tr key={row.id} className={entry.absent ? "bg-gray-50" : problem ? "bg-red-50/60" : undefined}>
                          <td className="sheet-rownum">{index + 1}</td>
                          <td className="min-w-[11rem]">
                            <span className="font-medium text-gray-900">{row.student_name}</span>
                            <span className="block text-xs text-gray-500">{row.student_number}</span>
                          </td>
                          <td className="text-gray-700">{row.class_name}</td>
                          <td className="tabular-nums text-gray-700">{row.seat_number || "—"}</td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              max={exam.total_marks}
                              step="0.5"
                              disabled={!editable || entry.absent}
                              value={entry.marks}
                              onChange={(e) => set(row.id, { marks: e.target.value })}
                              aria-label={`Mark for ${row.student_name}`}
                              aria-invalid={Boolean(problem)}
                              className={`input h-8 w-24 py-0 tabular-nums ${problem ? "border-red-400" : ""}`}
                            />
                            {problem && <span className="block text-xs text-red-700">{problem}</span>}
                          </td>
                          <td>
                            <input type="checkbox" disabled={!editable} checked={entry.absent} onChange={(e) => set(row.id, { absent: e.target.checked, marks: e.target.checked ? "" : entry.marks })} aria-label={`${row.student_name} was absent`} className="h-4 w-4 rounded border-gray-300" />
                          </td>
                          <td className="text-right font-semibold tabular-nums">{entry.absent ? <span className="font-normal text-gray-400">—</span> : grade}</td>
                          <td className="min-w-[12rem]">
                            <input disabled={!editable} value={entry.remarks} onChange={(e) => set(row.id, { remarks: e.target.value })} aria-label={`Remark for ${row.student_name}`} className="input h-8 py-0 text-xs" />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {editable && Boolean(candidates.data?.length) && (
              <div className="sticky bottom-0 z-20 pb-1 pt-2">
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-gray-200 bg-white/95 px-4 py-3 shadow-lg backdrop-blur">
                  <p className="text-sm text-gray-600">
                    {Object.keys(problems).length ? `${Object.keys(problems).length} mark${Object.keys(problems).length === 1 ? " is" : "s are"} out of range` : unsaved ? "Changes not saved yet" : summary && !summary.marked && !summary.absent ? "Nothing entered yet" : "Saved"}
                    {summary && summary.blank > 0 && ` · ${summary.blank} still blank`}
                  </p>
                  <div className="flex gap-2">
                    <button type="button" disabled={save.isPending || !unsaved || Object.keys(problems).length > 0} onClick={() => save.mutate()} className="btn btn-outline gap-1.5">
                      <Save className="h-4 w-4" /> {save.isPending ? "Saving…" : "Save the sheet"}
                    </button>
                    <button type="button" disabled={submit.isPending || unsaved || (summary?.blank ?? 1) > 0} title={unsaved ? "Save first." : summary?.blank ? "Every candidate needs a mark or an absence." : undefined} onClick={() => submit.mutate()} className="btn btn-primary gap-1.5">
                      <CheckCircle2 className="h-4 w-4" /> Submit for moderation
                    </button>
                  </div>
                </div>
                {[save, submit].map((mutation, index) => mutation.isError && <div key={index} className="mt-2">{errorLine(mutation.error, "That could not be done.")}</div>)}
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}
